import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { List, Map } from 'immutable';
import moment from 'moment';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import { UserDetailsPopup } from '../../components/Popup/UserDetailsPopup';
import {
  updateSelectedUser,
  updateOrInsertUser,
  updateTitle,
  updateSupervisor,
  updateModule,
  updateAttempts,
  closeSuggestion,
  openDropdownMenuTitles,
} from '../../actions/userManage';
import { usePopupContext } from '../../contexts/PopupContext';

const UNKNOWN_ERROR = 'Unknown error';

/**
 * Form field shown for an API error field, when their names differ.
 */
const API_ERROR_FIELDS = {
  moduleId: 'module',
  supervisorId: 'supervisor',
  titleId: 'title',
  roles: 'role',
};

/**
 * Split API validation errors into errors of the form fields and the common ones.
 *
 * @param {object} responseBody Error response body.
 * @param {Immutable.Map} selectedUser Selected user, its keys are the form fields.
 * @returns {{ fieldErrors: Immutable.Map, commonErrors: Immutable.List }} Errors.
 */
function getApiErrors(responseBody, selectedUser) {
  const errors = responseBody?.errors;

  if (!errors) {
    return { fieldErrors: Map(), commonErrors: List([responseBody?.message || UNKNOWN_ERROR]) };
  }

  return Object.entries(errors).reduce(
    ({ fieldErrors, commonErrors }, [field, messages]) => {
      const message = [].concat(messages).join(' ');
      const formField = API_ERROR_FIELDS[field] || field;

      if (selectedUser.has(formField) || formField === 'password' || formField === 'rePassword') {
        return { fieldErrors: fieldErrors.set(formField, message), commonErrors };
      }

      return { fieldErrors, commonErrors: commonErrors.push(message) };
    },
    { fieldErrors: Map(), commonErrors: List() },
  );
}

/**
 * Popup to add a new user or edit the selected one.
 *
 * @param {object} props Props.
 * @param {Immutable.Map} props.popup Popup data, its props: `userId` (edit only) and `onSave` (called after saving).
 */
const UserDetailsPopupContainer = props => {
  const {
    popup,
    selectedUser,
    formError,
    saveStatus,
    updateSelectedUser,
    updateOrInsertUser,
    updateModule,
    updateSupervisor,
    updateTitle,
    updateAttempts,
    closeSuggestion,
    openDropdownMenuTitles,
  } = props;
  const { closePopup, onClosePopup } = usePopupContext();
  const userId = popup.getIn(['props', 'userId']);
  const onSave = popup.getIn(['props', 'onSave']);
  const [apiErrors, setApiErrors] = useState(() => ({ fieldErrors: Map(), commonErrors: List() }));
  // Client validation errors are shown only after the first Save click.
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    onClosePopup(closeSuggestion);
  }, []);

  const clearApiError = useCallback(name => {
    setApiErrors(prev => ({ fieldErrors: prev.fieldErrors.delete(name), commonErrors: prev.commonErrors }));
  }, []);

  const handleFormChange = useCallback(
    event => {
      const { name, checked, value } = event.target;

      clearApiError(name);

      if (name === 'reset') {
        updateAttempts(checked);
      } else if (name === 'inactive') {
        const endDate = selectedUser.get('endDate', null);

        updateSelectedUser(name, checked);
        if (checked && !endDate) {
          // Set current date by default.
          updateSelectedUser('endDate', moment(new Date()));
        }

        if (!checked && endDate) {
          updateSelectedUser('endDate', '');
        }
      } else if (name === 'isMailingBacklog') {
        updateSelectedUser(name, checked);
      } else if (name === 'shareFirefliesMeetings') {
        updateSelectedUser(name, value === 'true');
      } else {
        updateSelectedUser(name, value, checked);
      }
    },
    [clearApiError, updateAttempts, selectedUser, updateSelectedUser],
  );

  /**
   * Update selected user hire or end date.
   *
   * @param {Moment} date Hire / Start / End date of selected user.
   * @param {string} fieldName Type of date ('hireDate' / 'startDate' / 'endDate').
   */
  const handleHireEndDateChange = useCallback(
    (date, fieldName) => {
      if (!fieldName) return;

      clearApiError(fieldName);
      updateSelectedUser(fieldName, date);
    },
    [clearApiError, updateSelectedUser],
  );

  const handleUserSuggestion = useCallback(
    ({ data: { name }, suggestion }) => {
      clearApiError(name);

      if (name === 'module') {
        updateModule(suggestion.get('id'), suggestion.get('name'));
      } else if (name === 'supervisor') {
        updateSupervisor(suggestion.get('id'), suggestion.get('name'));
      } else if (name === 'title') {
        updateTitle(suggestion.get('id'), suggestion.get('name'));
      }
    },
    [clearApiError, updateModule, updateSupervisor, updateTitle],
  );

  const handleSave = useCallback(() => {
    setIsSubmitted(true);

    if (saveStatus === 'error') return;

    const postingUser = selectedUser.toJS();

    if (!postingUser.supervisorId || postingUser.supervisorId < 0) {
      postingUser.supervisorId = null;
    }

    if (!postingUser.moduleId || postingUser.moduleId < 0) {
      delete postingUser.moduleId;
    }

    // Empty passwords mean the password is not changed.
    if (!postingUser.password && !postingUser.rePassword) {
      delete postingUser.password;
      delete postingUser.rePassword;
    }

    const isUpdateRequest = postingUser.id > 0;
    const startDate = postingUser.startDate ? moment(postingUser.startDate).format('YYYY-MM-DD') : null;
    const endDate = postingUser.endDate ? moment(postingUser.endDate).format('YYYY-MM-DD') : null;
    let hireDate = null;

    if (postingUser.hireDate) {
      hireDate = moment(postingUser.hireDate).format('YYYY-MM-DD');
    } else if (!isUpdateRequest) {
      hireDate = moment(new Date()).format('YYYY-MM-DD');
    }

    const formattedData = {
      // Transform moment() date to valid for backend string.
      ...postingUser,
      hireDate,
      startDate,
      endDate,
      shareFirefliesMeetings: Boolean(postingUser.shareFirefliesMeetings),
    };

    updateOrInsertUser(formattedData, {
      afterSuccess: () => {
        closePopup();
        onSave();
      },
      afterError: ({ responseBody }) => {
        setApiErrors(getApiErrors(responseBody, selectedUser));
      },
    });
  }, [saveStatus, selectedUser, updateOrInsertUser, closePopup, onSave]);

  const errors = useMemo(() => (isSubmitted ? formError : Map()).merge(apiErrors.fieldErrors), [
    isSubmitted,
    formError,
    apiErrors.fieldErrors,
  ]);
  const isUserLoading = Boolean(userId) && selectedUser.get('id') !== userId;
  const isSaving = saveStatus === 'saving';

  return (
    <UserDetailsPopup
      {...props}
      commonErrors={apiErrors.commonErrors}
      formError={errors}
      isNew={!userId}
      isSaveDisabled={isUserLoading || isSaving}
      loading={isUserLoading || isSaving}
      onCancel={closePopup}
      onFormChange={handleFormChange}
      onHireEndDateChange={handleHireEndDateChange}
      onSave={handleSave}
      onSuggestionInputTitleClick={openDropdownMenuTitles}
      onUserSuggestionClose={closeSuggestion}
      onUserSuggestionSelected={handleUserSuggestion}
    />
  );
};

UserDetailsPopupContainer.propTypes = {
  closeSuggestion: PropTypes.func.isRequired,
  formError: PropTypes.instanceOf(Map).isRequired,
  openDropdownMenuTitles: PropTypes.func.isRequired,
  popup: PropTypes.instanceOf(Map).isRequired,
  saveStatus: PropTypes.string.isRequired,
  selectedUser: PropTypes.instanceOf(Map).isRequired,
  updateAttempts: PropTypes.func.isRequired,
  updateModule: PropTypes.func.isRequired,
  updateOrInsertUser: PropTypes.func.isRequired,
  updateSelectedUser: PropTypes.func.isRequired,
  updateSupervisor: PropTypes.func.isRequired,
  updateTitle: PropTypes.func.isRequired,
};

const selectReports = createSelector(
  state => state.user.get('reports'),
  reports => (reports.toJS ? reports.toJS() : reports),
);

const mapStateToProps = state => ({
  suggestions: state.user.get('suggestions'),
  roles: state.user.get('roles'),
  dashboardUserTitles: state.user.get('dashboardUserTitles'),
  formMode: state.user.get('formMode'),
  formError: state.user.get('formError'),
  selectedUser: state.user.get('selectedUser'),
  saveStatus: state.user.get('saveStatus'),
  titleSuggestionMode: state.user.get('titleSuggestionMode'),
  moduleSuggestionMode: state.user.get('moduleSuggestionMode'),
  supervisorSuggestionMode: state.user.get('supervisorSuggestionMode'),
  reports: selectReports(state),
});

const mapDispatchToProps = {
  updateSelectedUser,
  updateOrInsertUser,
  updateTitle,
  updateSupervisor,
  updateModule,
  updateAttempts,
  closeSuggestion,
  openDropdownMenuTitles,
};

export { UserDetailsPopupContainer };
export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
  connectOptions,
)(memo(UserDetailsPopupContainer));
